import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { message, Upload, Button, Image } from "antd";
import { Helmet } from "react-helmet";
import { Layout } from "../pages/app/Layout";
import { Loader } from "../components/Loader";
import { PBox, PH4, PImg, PText, PH2, PH1 } from "../theme/BaseTheme";
import { refetchUser } from "../../actions/auth";
import ChangePlanModal from "../components/app/ChangePlanModal";
import PaymentConfirmModal from "../components/app/PaymentConfirmModal";
import { applyImageFilter } from "../../actions/imageFilter";
import ImageFilterDownloadModal from "./ImageFilterDownloadModal";
import { UploadErrorMessages } from "../../data/messages";
import { ImgComparisonSlider } from "@img-comparison-slider/react";
import GhibliFilter from "./landings/GhibliFilter";
import SketchFilter from "./landings/SketchFilter";
import ComicFilter from "./landings/ComicFilter";
import DisneyFilter from "./landings/DisneyFilter";
import MangaFilter from "./landings/MangaFilter";
import OilPaintingFilter from "./landings/OilPaintingFilter";
import PixelArtFilter from "./landings/PixelArtFilter";
import RickAndMortyFilter from "./landings/RickAndMortyFilter";
import RococoFilter from "./landings/RococoFilter";
import SimpsonFilter from "./landings/SimpsonFilter";
import ExploreFilters from "./ExploreFilters";

const ImageFilterCreditCost = 4;

const filterTypeToImage = {
    comic: "/images/image-filters/comic-filter-cover.webp",
    disney: "/images/image-filters/disney-filter-cover.webp",
    ghibli: "/images/image-filters/ghibli-filter-cover.webp",
    manga: "/images/image-filters/manga-filter-cover.webp",
    "oil-painting": "/images/image-filters/oil-painting-filter-cover.webp",
    "pixel-art": "/images/image-filters/pixel-art-filter-cover.webp",
    "rick-and-morty": "/images/image-filters/rick-and-morty-filter-cover.webp",
    rococo: "/images/image-filters/rococo-filter-cover.webp",
    simpson: "/images/image-filters/simpson-filter-cover.webp",
    sketch: "/images/image-filters/sketch-filter-cover.webp",
};

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const ImageFilterApp = (props) => {
    const highlightRef = useRef();
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState("");
    const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
    const [isPaymentConfirmModalOpen, setIsPaymentConfirmModalOpen] =
        useState(false);
    const [newPlan, setNewPlan] = useState("");
    const [shouldScrollToResult, setShouldScrollToResult] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    // Get filter type from URL path
    const filterType = props.location.pathname
        .split("/")[1]
        .replace("ai-", "")
        .replace("-filter", "");
    const formattedFilterType = filterType
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    const handleUpload = async ({ file }) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setUploadedImage(file.url || file.preview);
    };

    useEffect(() => {
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (shouldScrollToResult && highlightRef.current) {
            highlightRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            setShouldScrollToResult(false);
        }
    }, [shouldScrollToResult]);

    const handleDownloadModalOk = () => {
        setIsDownloadModalOpen(false);
    };

    const handleDownloadModalCancel = () => {
        setIsDownloadModalOpen(false);
    };

    const renderContent = () => {
        if (isLoading) {
            return <Loader />;
        }
        return (
            <Layout>
                <Helmet>
                    <title>{formattedFilterType} Filter | Patternful AI</title>
                    <meta
                        name="description"
                        content={`Transform your photos with our AI-powered ${formattedFilterType} filter.`}
                    />
                    <meta
                        name="keywords"
                        content={`${formattedFilterType} Filter, AI Filter, Image Filter, Photo Filter, AI Image Editing, Patternful, Patternful AI`}
                    />
                </Helmet>
                <PBox css={styles}>
                    <PBox className="app-content">
                        <PBox className="app-content-title">
                            <PH1>AI {formattedFilterType} Filter</PH1>
                            <PText>
                                Transform your photos with our AI-powered{" "}
                                {formattedFilterType.toLowerCase()} filter
                                style. Create stunning images instantly.
                            </PText>
                        </PBox>
                        <PBox className="app-content-control">
                            <PBox className="outline-maker-upload-image">
                                <PBox className="input-section">
                                    <PImg
                                        alt="patternful ai"
                                        src={filterTypeToImage[filterType]}
                                        style={{
                                            width: "100%",
                                            height: "24rem",
                                            borderRadius: "1rem",
                                        }}
                                    />
                                </PBox>

                                <PBox className="upload-section">
                                    <PBox className="outline-maker-upload">
                                        <Upload.Dragger
                                            onChange={async (info) => {
                                                await handleUpload(info);
                                            }}
                                            showUploadList={false}
                                            listType="picture-card"
                                            maxCount={1}
                                            beforeUpload={async (file) => {
                                                const isJpgOrPng =
                                                    file.type ===
                                                        "image/jpeg" ||
                                                    file.type === "image/png";
                                                if (!isJpgOrPng) {
                                                    message.error(
                                                        UploadErrorMessages.JPG_OR_PNG
                                                    );
                                                }
                                                const isLt4M =
                                                    file.size / 1024 / 1024 < 4;
                                                if (!isLt4M) {
                                                    message.error(
                                                        UploadErrorMessages.IMAGE_SIZE_4MB
                                                    );
                                                }
                                                return (
                                                    (isJpgOrPng && isLt4M) ||
                                                    Upload.LIST_IGNORE
                                                );
                                            }}
                                        >
                                            {uploadedImage !== "" && (
                                                <PImg
                                                    className="outline-maker-image"
                                                    src={uploadedImage}
                                                />
                                            )}
                                            {uploadedImage === "" && (
                                                <PText>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="#000000"
                                                        viewBox="0 0 256 256"
                                                    >
                                                        <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM93.66,85.66,120,59.31V152a8,8,0,0,0,16,0V59.31l26.34,26.35a8,8,0,0,0,11.32-11.32l-40-40a8,8,0,0,0-11.32,0l-40,40A8,8,0,0,0,93.66,85.66Z"></path>
                                                    </svg>
                                                    <br />
                                                    Click to upload your image,
                                                    <br />
                                                    or drag and drop your image
                                                    into this area.
                                                </PText>
                                            )}
                                        </Upload.Dragger>
                                    </PBox>
                                </PBox>
                            </PBox>
                            <PBox className="generate-button-container">
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        if (
                                            props.userAccount?.credits +
                                                props.userAccount
                                                    ?.roll_over_credits <
                                            ImageFilterCreditCost
                                        ) {
                                            message.error(
                                                "Your credit balance is not enough. Please consider upgrading your plan or buying more credits.",
                                                3
                                            );
                                        } else {
                                            if (uploadedImage === "") {
                                                message.error(
                                                    "Please upload an image. ",
                                                    3
                                                );
                                            } else {
                                                setIsGenerating(true);
                                                message.open({
                                                    key: "apply_image_filter",
                                                    type: "loading",
                                                    content:
                                                        "Start applying image filter. This can take a while.",
                                                    duration: 0,
                                                });
                                                await props.applyImageFilter(
                                                    uploadedImage,
                                                    formattedFilterType
                                                );
                                                message.destroy(
                                                    "apply_image_filter"
                                                );
                                                await props.refetchUser();
                                                setIsGenerating(false);
                                                setShouldScrollToResult(true);
                                            }
                                        }
                                    }}
                                    loading={isGenerating}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    Generate{" "}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#000000"
                                        viewBox="0 0 256 256"
                                        style={{
                                            fill: "#d48806",
                                            backgroundColor: "#ffe58f",
                                            padding: "0.125rem 0.25rem",
                                            borderRadius: "0.25rem",
                                            width: "1rem",
                                            height: "1rem",
                                            marginLeft: "0.5rem",
                                            marginRight: "0.25rem",
                                        }}
                                    >
                                        <path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16Zm-52-56H92a28,28,0,0,0,0,56h72a28,28,0,0,0,0-56Zm-28,16v24H120V152ZM80,164a12,12,0,0,1,12-12h12v24H92A12,12,0,0,1,80,164Zm84,12H152V152h12a12,12,0,0,1,0,24ZM72,108a12,12,0,1,1,12,12A12,12,0,0,1,72,108Zm88,0a12,12,0,1,1,12,12A12,12,0,0,1,160,108Z"></path>
                                    </svg>
                                    x{ImageFilterCreditCost}
                                </Button>
                                <Button type="ghost" href="/filtered-images/1">
                                    View My Filtered Images
                                </Button>
                            </PBox>

                            {props.generatedImage !== undefined && (
                                <>
                                    <PBox
                                        className="generate-outline-content"
                                        ref={highlightRef}
                                    >
                                        <PBox className="binary-mask-sketch-canva">
                                            <Image
                                                src={props.generatedImage}
                                                crossOrigin={
                                                    process.env.NODE_ENV ===
                                                    "development"
                                                        ? "anonymous"
                                                        : ""
                                                }
                                            />
                                        </PBox>
                                    </PBox>{" "}
                                    <PBox className="generate-button-container">
                                        <Button
                                            type="primary"
                                            onClick={async () => {
                                                setIsDownloadModalOpen(true);
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </PBox>
                                </>
                            )}
                            <ExploreFilters />
                            {filterType === "comic" && <ComicFilter />}
                            {filterType === "disney" && <DisneyFilter />}
                            {filterType === "ghibli" && <GhibliFilter />}
                            {filterType === "manga" && <MangaFilter />}
                            {filterType === "oil-painting" && (
                                <OilPaintingFilter />
                            )}
                            {filterType === "pixel-art" && <PixelArtFilter />}
                            {filterType === "rick-and-morty" && (
                                <RickAndMortyFilter />
                            )}
                            {filterType === "rococo" && <RococoFilter />}
                            {filterType === "simpson" && <SimpsonFilter />}
                            {filterType === "sketch" && <SketchFilter />}
                        </PBox>
                        {isChangePlanModalOpen && (
                            <ChangePlanModal
                                isChangePlanModalOpen={isChangePlanModalOpen}
                                handleChangePlanModalOk={async () => {
                                    setIsPaymentConfirmModalOpen(true);
                                    setIsChangePlanModalOpen(false);
                                }}
                                handleChangePlanModalCancel={() => {
                                    setIsChangePlanModalOpen(false);
                                }}
                                setNewPlan={(value) => {
                                    setNewPlan(value);
                                }}
                            />
                        )}
                        {isDownloadModalOpen && (
                            <ImageFilterDownloadModal
                                filteredImageUUID={props.uuid}
                                isDownloadModalOpen={isDownloadModalOpen}
                                handleDownloadModalOk={handleDownloadModalOk}
                                handleDownloadModalCancel={
                                    handleDownloadModalCancel
                                }
                            />
                        )}
                        {isPaymentConfirmModalOpen && (
                            <PaymentConfirmModal
                                newPlan={newPlan}
                                isPaymentConfirmModalOpen={
                                    isPaymentConfirmModalOpen
                                }
                                handlePaymentConfirmModalOk={() =>
                                    setIsPaymentConfirmModalOpen(false)
                                }
                                handlePaymentConfirmModalCancel={() =>
                                    setIsPaymentConfirmModalOpen(false)
                                }
                            />
                        )}
                    </PBox>
                </PBox>
            </Layout>
        );
    };

    return renderContent();
};

const styles = {
    minHeight: "48rem",
    "& .app-content-title": {
        "& h1": {
            textAlign: "center",
        },
        "& p": {
            textAlign: "center",
        },
        margin: "4rem auto",
    },
    "& .app-content-control": {
        margin: "0 auto",
    },
    "& .generate-button-container": {
        display: "flex",
        justifyContent: "center",
        margin: "2rem 0",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .outline-maker-upload-image": {
        margin: "2rem auto 2rem auto",
        maxWidth: "64rem",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2rem",
        alignItems: "start",
        "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
            gap: "1rem",
        },

        "& .upload-section": {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        "& .input-section": {
            display: "flex",
            flexDirection: "column",
            height: "24rem",
            "& .control-section-label": {
                fontWeight: "600",
                fontSize: "14px",
                color: "$colors$text",
                marginBottom: "1rem",
            },
            "& .background-options-grid": {
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: "1rem",
                height: "130px",
                padding: "0.5rem",
                border: "1px solid $colors$borderColor",
                borderRadius: "8px",
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                        background: "#555",
                    },
                },
                "& .background-option": {
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s ease",
                    borderRadius: "8px",
                    padding: "0.5rem",
                    border: "2px solid transparent",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                    "&.selected": {
                        border: "2px solid $colors$primary",
                        backgroundColor: "rgba(24, 144, 255, 0.1)",
                    },
                    "& img": {
                        width: "100%",
                        height: "80px",
                        objectFit: "contain",
                        borderRadius: "4px",
                        marginBottom: "0.5rem",
                    },
                    "& span": {
                        display: "block",
                        fontSize: "12px",
                        color: "$colors$text",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },
                },
            },
        },
        "& .outline-maker-upload": {
            marginBottom: "1rem",
            width: "100%",
            "& svg": {
                width: "3rem",
                height: "3rem",
                fill: "$colors$primary",
            },
            "& span": {
                padding: "0",
                width: "100%",
                height: "24rem",
                display: "block",
                margin: "0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
        },
        "& .outline-maker-image": {
            maxWidth: "100%",
            maxHeight: "14rem",
            margin: "auto 1rem",
            objectFit: "contain",
        },
        "& h2": {
            marginBottom: "3rem",
        },
    },
    "& .remove-background-download-buttons": {
        margin: "5rem auto",
        display: "flex",
        justifyContent: "center",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .remove-background-content": {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "2rem",
        marginTop: "8rem",
        marginBottom: "4rem",
        "& button": {
            width: "100%",
        },
    },
    "& .binary-mask-sketch-canva": {
        display: "block",
        "& img": {
            display: "block",
            margin: "auto",
            width: `calc(31rem - 2px)`,
        },
    },
    "& .binary-mask-brush-size": {
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "1rem",
        "& h4": { marginLeft: "0.5rem" },
    },
    "& .binary-mask-result-image": {
        margin: "6rem auto 0 auto",
        "& img": {
            height: "40rem",
            margin: "0 auto",
        },
    },
    "& .upscale-option": {
        display: "flex",
        alignItems: "center",
    },
    "& .caption-before": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        left: "12px",
    },
    "& .caption-after": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        right: "12px",
    },
    "& .slider-with-animated-handle": {
        outline: "0",
    },
    "& .remove-background-slider": {
        width: "60rem",
        height: "40rem",
        border: "1px solid $colors$borderColor",
        position: "relative",
        borderRadius: "0.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
        "& img": {
            //width: "30rem",
            maxHeight: "40rem",
            margin: "0 auto",
        },
    },
    "& .generate-outline-content": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "8rem auto 2rem auto",
        "& img": {
            maxHeight: "40rem",
        },
    },
};

function mapStateToProps(state) {
    return {
        userAccount: state.authReducer?.userAuth?.userAccount,
        generatedImage:
            state.imageFilterReducer?.applyImageFilter?.generatedImage,
        uuid: state.imageFilterReducer?.applyImageFilter?.uuid,
    };
}

export default connect(mapStateToProps, {
    refetchUser,
    applyImageFilter,
})(withRouter(ImageFilterApp));
